/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

/**
 * The only frontend knowledge of the Schedule_Tag grammar.
 *
 * `buildScheduleValue` turns the structured state the Schedule_Modal holds into a tag value, and
 * `parseScheduleValue` turns a tag value back into that structured state. They are exact inverses
 * over canonical models and are property-tested as such, which is why they live in one file
 * (Requirement 16.4): separating inverses is how inverses drift.
 *
 * Two things this module deliberately does not do:
 *
 * - It does not validate (Requirement 16.5). The backend's `schedule-tag-validation.js` is the
 *   authoritative validator and runs on every write. The frontend prevents invalid input
 *   structurally, by only offering real day names, real group names and interval-aligned times, and
 *   surfaces backend validation errors rather than reimplementing the rules. `parseScheduleValue` is
 *   therefore tolerant: it never throws, and it skips a token it cannot read rather than rejecting
 *   the value, because the tag may have been written outside Service Workbench.
 * - It does not format for display (Requirement 16.11). Human-readable window lists, dropdown
 *   option lists and timezone rendering all live in `schedule-display.js`.
 *
 * The grammar, per `docs/TAG-CONTRACT.md` in the ec2-scheduler repository and as transcribed by the
 * backend validator:
 *
 *   Group_List       `day,weekend`                  no colon anywhere in the value
 *   Custom_Schedule  `MON:09:00-17:00;SAT:ON`       `;` between entries, `DAY:VALUE` per entry
 *   No schedule      ``                             the empty value; the tag is removed
 *
 * A day value is `ON` (the whole day), `OFF` (no window), or an `HH:MM-HH:MM` range. `OFF` and simply
 * omitting the day mean the same thing to the scheduler, so the builder omits the day; the parser reads
 * both, since the value it is handed is whatever is on the instance.
 *
 * A colon anywhere in the value selects the Custom_Schedule grammar, which is the same rule the
 * External_Scheduler applies, so a group name may never contain a colon.
 */

// Exported so that no component hardcodes the day sequence. Monday first, matching the contract's
// worked examples and the order the day grid renders.
const DAY_ORDER = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

// The two literal day values of the grammar, matching the backend validator's tokens. `ON` is the whole
// calendar day; `OFF` is no window. The builder emits neither `OFF` nor an entry for an off day, because
// the contract makes the two equivalent, but the parser has to read `OFF` because the tag may have been
// written by hand or by another client.
const ALL_DAY_TOKEN = 'ON';
const OFF_TOKEN = 'OFF';

const ENTRY_SEPARATOR = ';';
const GROUP_SEPARATOR = ',';
const RANGE_SEPARATOR = '-';

// The times a day row carries while it is off or set to all day. They are never emitted, but they
// have to be something, and they have to be the same something on both sides of a round trip.
const DEFAULT_START = '09:00';
const DEFAULT_STOP = '17:00';

const DAY_KIND_OFF = 'off';
const DAY_KIND_ALL = 'all';
const DAY_KIND_RANGE = 'range';

const MODE_NONE = 'none';
const MODE_GROUPS = 'groups';
const MODE_CUSTOM = 'custom';

/**
 * A `days` map with every day off, which is also the state the modal opens in for a workspace with
 * no timetable. Returned fresh each call so callers can mutate their copy freely.
 */
function defaultDays() {
  return DAY_ORDER.reduce((acc, day) => {
    acc[day] = { kind: DAY_KIND_OFF, start: DEFAULT_START, stop: DEFAULT_STOP };
    return acc;
  }, {});
}

/**
 * Normalises an `HH:MM` token to zero-padded canonical form.
 *
 * Tolerant by design: anything it cannot read as two numeric components is returned trimmed and
 * otherwise untouched, because deciding that a time is unacceptable is the validator's job.
 */
function normaliseTime(value) {
  if (typeof value !== 'string') return value === null || value === undefined ? '' : String(value);

  const trimmed = value.trim();
  const components = trimmed.split(':');
  if (components.length !== 2) return trimmed;

  const [hourToken, minuteToken] = components;
  if (!/^\d{1,2}$/.test(hourToken) || !/^\d{1,2}$/.test(minuteToken)) return trimmed;

  return `${hourToken.padStart(2, '0')}:${minuteToken.padStart(2, '0')}`;
}

/**
 * Builds the Schedule_Tag value for a schedule model.
 *
 * Emits the canonical form: uppercase day names, zero-padded `HH:MM`, `;` between custom entries,
 * `,` between group names, and no whitespace anywhere.
 *
 * Returns the empty string, meaning "remove the tag", for mode `none`, and also for a model that
 * expresses no window at all: mode `groups` with nothing selected, or mode `custom` with every day
 * off. Those two states have no canonical value, so they build to the empty string and parse back as
 * mode `none`.
 *
 * @param {{ mode: string, selectedGroups: string[], days: Object }} model
 * @returns {string}
 */
function buildScheduleValue({ mode, selectedGroups, days } = {}) {
  if (mode === MODE_GROUPS) {
    const names = (selectedGroups || [])
      .map(name => (typeof name === 'string' ? name.trim() : ''))
      .filter(name => name.length > 0);

    return names.join(GROUP_SEPARATOR);
  }

  if (mode === MODE_CUSTOM) {
    const source = days || {};
    const entries = [];

    DAY_ORDER.forEach(day => {
      const entry = source[day];
      if (!entry) return;

      if (entry.kind === DAY_KIND_ALL) {
        entries.push(`${day}:${ALL_DAY_TOKEN}`);
        return;
      }
      if (entry.kind === DAY_KIND_RANGE) {
        entries.push(`${day}:${normaliseTime(entry.start)}${RANGE_SEPARATOR}${normaliseTime(entry.stop)}`);
      }
    });

    return entries.join(ENTRY_SEPARATOR);
  }

  // mode 'none', and anything unrecognised, means no timetable.
  return '';
}

/**
 * Parses a Schedule_Tag value into the structured form the modal's controls bind to.
 *
 * The exact inverse of `buildScheduleValue` for canonical values. For non-canonical ones it is
 * tolerant rather than strict, since the tag may have been written outside Service Workbench:
 * whitespace and case are normalised, and an entry that cannot be read is skipped.
 *
 * Group names are matched case-insensitively against `groupNames` and returned with the casing the
 * group actually has, so that the returned names match the option values the group list renders. A
 * name with no match is returned as given rather than dropped, so the modal can show what is
 * actually set; callers wanting to flag those can compare against `groupNames` themselves.
 *
 * @param {string} value - The raw Schedule_Tag value; `''`, null and undefined all mean no timetable
 * @param {string[]} [groupNames] - The group names from the SchedulingInfo response
 * @returns {{ mode: string, selectedGroups: string[], days: Object }}
 */
function parseScheduleValue(value, groupNames) {
  const days = defaultDays();

  if (typeof value !== 'string') return { mode: MODE_NONE, selectedGroups: [], days };

  const trimmed = value.trim();
  if (trimmed.length === 0) return { mode: MODE_NONE, selectedGroups: [], days };

  // A colon anywhere selects the Custom_Schedule grammar, matching the backend's rule.
  if (!trimmed.includes(':')) {
    return { mode: MODE_GROUPS, selectedGroups: parseGroupList(trimmed, groupNames), days };
  }

  trimmed.split(ENTRY_SEPARATOR).forEach(rawEntry => {
    const entry = rawEntry.trim();
    if (entry.length === 0) return;

    // Split on the first colon only: the value itself contains colons in the HH:MM-HH:MM form.
    const separatorIndex = entry.indexOf(':');
    if (separatorIndex === -1) return;

    const day = entry
      .slice(0, separatorIndex)
      .trim()
      .toUpperCase();
    if (!DAY_ORDER.includes(day)) return;

    const rest = entry.slice(separatorIndex + 1).trim();

    const dayValue = rest.toUpperCase();

    if (dayValue === ALL_DAY_TOKEN) {
      days[day] = { kind: DAY_KIND_ALL, start: DEFAULT_START, stop: DEFAULT_STOP };
      return;
    }

    // `OFF` is set explicitly rather than left to the default, so a later `WED:OFF` overrides an earlier
    // `WED:09:00-17:00` the way the scheduler does, where a duplicate day silently wins over earlier ones.
    if (dayValue === OFF_TOKEN) {
      days[day] = { kind: DAY_KIND_OFF, start: DEFAULT_START, stop: DEFAULT_STOP };
      return;
    }

    const rangeParts = rest.split(RANGE_SEPARATOR);
    if (rangeParts.length !== 2) return;

    days[day] = {
      kind: DAY_KIND_RANGE,
      start: normaliseTime(rangeParts[0]),
      stop: normaliseTime(rangeParts[1]),
    };
  });

  return { mode: MODE_CUSTOM, selectedGroups: [], days };
}

/**
 * Splits a Group_List value into canonically cased group names.
 */
function parseGroupList(trimmed, groupNames) {
  const canonicalByLower = (groupNames || []).reduce((acc, name) => {
    if (typeof name !== 'string') return acc;
    acc[name.trim().toLowerCase()] = name.trim();
    return acc;
  }, {});

  return trimmed
    .split(GROUP_SEPARATOR)
    .map(name => name.trim())
    .filter(name => name.length > 0)
    .map(name => canonicalByLower[name.toLowerCase()] || name);
}

export {
  buildScheduleValue,
  parseScheduleValue,
  defaultDays,
  DAY_ORDER,
  ALL_DAY_TOKEN,
  OFF_TOKEN,
  DEFAULT_START,
  DEFAULT_STOP,
  DAY_KIND_OFF,
  DAY_KIND_ALL,
  DAY_KIND_RANGE,
  MODE_NONE,
  MODE_GROUPS,
  MODE_CUSTOM,
};
